import React, { useState, useEffect, useRef } from 'react';
import MediumButton from '../ui/MediumButton';
import { ArrowUpIcon } from '../../utils/icons';

const PromptInput = ({ onSubmit, placeholder = "eg: make it sound happier, help me finish, etc.", className = "", autoFocus = false, requireInput = false, showTimer = false, timerDuration = 5, fontSize }) => {
  const [value, setValue] = useState('');
  const [timeLeft, setTimeLeft] = useState(timerDuration);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [showTimerRing, setShowTimerRing] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const handleSubmit = () => {
    if (onSubmit && (!requireInput || value.trim())) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      setIsTimerActive(false);
      setShowTimerRing(false);
      onSubmit(value.trim() || "");
      setValue('');
    }
  };

  const handleInputChange = (e) => {
    setValue(e.target.value);
    
    // Cancel timer when user starts typing
    if (e.target.value.length > 0 && isTimerActive) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      setIsTimerActive(false);
      setShowTimerRing(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (showTimer && !isTimerActive) {
      setIsTimerActive(true);
      setShowTimerRing(true);
      setTimeLeft(timerDuration);
      
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 0.1) {
            handleSubmit();
            return 0;
          }
          return prev - 0.1;
        });
      }, 100);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [showTimer, timerDuration]);

  const timerProgress = showTimer ? Math.max(0, Math.min(1, timeLeft / timerDuration)) : 1;
  
  // Calculate the path length for the specific arc path
  const pathLength = 119.1; // Approximate path length for the arc
  const strokeDashoffset = pathLength * (1 - timerProgress);

  return (
    <div className={`flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 p-2 prompt-input-container ${className}`}>
      <div className="flex justify-start items-center self-stretch flex-grow-0 flex-shrink-0 relative overflow-hidden gap-4 pl-4 pr-2 py-2 rounded-lg bg-[#363030] shadow-[0px_4px_18px_0_rgba(0,0,0,0.1)]">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          className="flex-grow w-[287px] text-xs font-medium text-left bg-transparent text-white placeholder:text-[#726e6c] focus:outline-none caret-[#FD429C]"
          style={{
            fontFamily: 'PP Neue Montreal',
            fontWeight: 500,
            ...(fontSize && { fontSize })
          }}
        />
        <div className="relative w-10 h-10">
          <MediumButton
            onClick={handleSubmit}
            variant="primary"
            className={`w-8 h-8 absolute left-1 top-1 ${requireInput && !value.trim() ? 'opacity-30 cursor-not-allowed' : ''}`}
            leftIcon={<ArrowUpIcon />}
          />
          {showTimerRing && (
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"

              style={{ transform: 'rotate(-90deg)', width: '40px', height: '40px', position: 'absolute', left: '0px', top: '0px' }}
            >
              <path
                d="M39 20C39 17.5049 38.5086 15.0342 37.5537 12.729C36.5989 10.4238 35.1993 8.32929 33.435 6.56498C31.6707 4.80066 29.5762 3.40113 27.271 2.44629C24.9658 1.49145 22.4951 1 20 1C17.5049 0.999999 15.0342 1.49145 12.729 2.44629C10.4238 3.40112 8.32929 4.80065 6.56498 6.56497C4.80066 8.32928 3.40113 10.4238 2.44629 12.729C1.49145 15.0342 1 17.5049 1 20C0.999999 22.4951 1.49145 24.9658 2.44629 27.271C3.40113 29.5762 4.80065 31.6707 6.56497 33.435C8.32928 35.1993 10.4238 36.5989 12.729 37.5537C15.0342 38.5085 17.5049 39 20 39C22.4951 39 24.9658 38.5086 27.271 37.5537C29.5762 36.5989 31.6707 35.1993 33.435 33.435C35.1993 31.6707 36.5989 29.5762 37.5537 27.271C38.5086 24.9658 39 22.4951 39 20L39 20Z"
                stroke="white"
                strokeWidth="2"
                strokeDasharray={pathLength}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 0.1s ease-out' }}
              />
              <circle 
                cx="20" 
                cy="20" 
                r="19" 
                stroke="white" 
                strokeOpacity="0.3" 
                strokeWidth="2"
                fill="none"
              />
            </svg>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromptInput;